import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';

import ArticlesApi from '../../../Api/CRM/Modules/ArticlesApi';
import Article from '../../../Api/Models/Article';
import useGlobalState from '../../GlobalStateContext';
import Card from '../../elements/common/Card';
import handleValidationErrors, { ValidationErrors } from '../../helpers/ResponseErrorHelper';
import { StringParams } from '../../helpers/types';
import useBlockingSubmit from '../../hooks/useBlockingSubmit';
import useSmartField, { FieldChangeCallback } from '../../hooks/useSmartField';

import SeoTag from 'Api/Models/SeoTag';
import { articleSeoChecker } from 'crm/elements/formElements/ArticleSeoChecker';
import FormSuggestion, { Suggestions, SuggestionType } from 'crm/elements/formElements/FormSuggestion';
import { SelectOptions } from 'crm/elements/formElements/Select';
import useOnce from 'crm/hooks/useOnce';

function CreateArticle() {
    const history = useHistory();
    const { languageNames, baseLanguageId } = useGlobalState();
    const params = new URLSearchParams(window.location.search);
    const partial = useParams<StringParams>().partial === 'partial';
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [suggestions, setSuggestions] = useState<Suggestions>({});

    const [article, setArticle] = useState<Article>(
        new Article({
            is_partial: partial,
            title: params.get('title'),
            slug: params.get('slug'),
            language_id: params.get('language_id') ? parseInt(params.get('language_id')) : baseLanguageId,
            translation_id: params.get('translation_id') ? parseInt(params.get('translation_id')) : undefined,
            is_casino_page: params.get('is_casino_page') === '1',
            is_blog: params.get('is_blog') === '1',
            no_deletable: params.get('no_deletable') === '1',
            no_index: params.get('no_index') === '1',
            seo_tag: new SeoTag({}),
            publication_date: moment().format('YYYY-MM-DD HH:mm:ss'),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
    );
    const [translationLists, setTranslationLists] = useState<SelectOptions[]>([[], []]);

    useOnce(() => Promise.all([0, 1].map((p) => ArticlesApi.getTranslations(!!p))).then(setTranslationLists));

    const addSuggestion = (key: string, reason: string, value: string, type: SuggestionType) => {
        setSuggestions((prevData) => {
            const updatedData = {
                ...prevData,
                [key]: {
                    ...prevData[key], // Preserve existing reasons
                    [reason]: [{ value, type }],
                },
            };
            return updatedData;
        });
    };

    useEffect(() => {
        articleSeoChecker({ article, addSuggestion });
    }, [article]);

    const handleFieldChange: FieldChangeCallback = (name, value) => {
        if (!name.startsWith('seo_tag.')) {
            setArticle((prevArticle) => prevArticle.getNew({ [name]: value }));
            return;
        }

        // Extract the field name (e.g., "title" from "seo_tag.title")
        const seoTagField = name.replace('seo_tag.', '');

        setArticle((prevArticle) =>
            prevArticle.getNew({
                seo_tag: new SeoTag({
                    ...prevArticle.seo_tag,
                    [seoTagField]: value, // Correctly updating only the specified field
                }),
            })
        );
    };

    const Field = useSmartField({ errors, onChange: handleFieldChange });

    const [Submit, submitBlocked] = useBlockingSubmit();

    async function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        try {
            await ArticlesApi.store(article);
            await swal({ title: 'Success', text: 'Article created correctly', icon: 'success' });
            history.push(`/articles/${article.is_partial ? 'partials' : ''}`);
        } catch (error) {
            handleValidationErrors(error, setErrors);
            submitBlocked(false);
        }
    }

    const { loggedAdmin } = useGlobalState();

    return (
        <Card>
            <form onSubmit={handleFormSubmit}>
                {!partial && (
                    <div>
                        <h2>Seo Tag</h2>
                        <Field name="seo_tag.title" value={article.seo_tag.title} />
                        <FormSuggestion suggestions={suggestions.seo_tag_title} />
                        <Field name="seo_tag.meta_description" value={article.seo_tag.meta_description} />
                        <FormSuggestion suggestions={suggestions.seo_tag_meta_description} />
                        <Field name="seo_tag.meta_keywords" value={article.seo_tag.meta_keywords} />
                        <Field name="seo_tag.og_description" value={article.seo_tag.og_description} />
                        <FormSuggestion suggestions={suggestions.seo_tag_og_description} />
                        <Field name="seo_tag.og_image" value={article.seo_tag.og_image} />
                        <Field name="seo_tag.og_url" value={article.seo_tag.og_url} />
                        <Field name="seo_tag.og_type" value={article.seo_tag.og_type} />
                    </div>
                )}
                <Field name="title" value={article.title} required />
                <FormSuggestion suggestions={suggestions.title} />
                <Field type="checkbox" name="is_title_hidden" value={article.is_title_hidden} />
                <Field name="slug" value={article.slug} required />
                <FormSuggestion suggestions={suggestions.slug} />
                <Field
                    type="selectNumber"
                    name="language_id"
                    value={article.language_id}
                    options={languageNames}
                    label="Language"
                    required
                />
                <Field
                    type="selectNumber"
                    name="translation_id"
                    value={article.translation_id}
                    options={translationLists[+article.is_partial]}
                    label="Base article of Translation ID"
                    placeholder="— not exists (create a new Translation ID)"
                />
                <Field type="checkbox" name="is_blog" value={article.is_blog} />
                <Field type="checkbox" name="is_casino_page" value={article.is_casino_page} />
                <Field type="texteditor" name="text" value={article.text} />
                <FormSuggestion suggestions={suggestions.text} />
                <Field type="checkbox" name="is_partial" value={article.is_partial} label="System Pages Article" />
                <Field type="checkbox" name="no_deletable" value={article.no_deletable} />
                <Field
                    type="checkbox"
                    name="no_index"
                    value={article.no_index}
                    disabled={!loggedAdmin.isSuperAdmin()}
                />
                <Field type="datetime-local" name="publication_date" value={article.publication_date} />
                <Submit label="Create" />
            </form>
        </Card>
    );
}

export default CreateArticle;
